// frontend/src/components/ProductsList.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Edit, Trash2, Plus, Building, Package,
  Filter, Search, Loader2, Eye,
  Globe, Tag, Scale, BarChart3, DollarSign,
  Calculator, ShoppingCart, TrendingUp,
  AlertCircle, CheckCircle, XCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./ProductsList.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const ProductsList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter that uses company's currency
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProductType, setSelectedProductType] = useState("");
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [inventorySummary, setInventorySummary] = useState(null);

  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  // Product type options
  const productTypes = ["All", "Stock", "Non-Stock"];

  useEffect(() => {
    fetchProducts();
    fetchInventorySummary();

    if (isSystemAdmin) {
      fetchCompanies();
    }

    fetchCategoriesForFilter();
  }, [selectedCompany, selectedCategory, selectedProductType, searchTerm]);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      let endpoint = `${url}/api/products`;
      const params = {};

      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }

      if (selectedCategory) {
        params.category_id = selectedCategory;
      }

      if (selectedProductType && selectedProductType !== "All") {
        params.product_type = selectedProductType;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await axios.get(endpoint, { params });

      if (response.data.success) {
        setProducts(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch products");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchCategoriesForFilter = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${url}/api/productcategory`, {
        params: { company_id: isSystemAdmin ? selectedCompany : (company?._id || company) }
      });

      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchInventorySummary = async () => {
    try {
      const response = await axios.get(`${url}/api/products/inventory/summary`, {
        params: { company_id: isSystemAdmin ? selectedCompany : undefined }
      });

      if (response.data.success) {
        setInventorySummary(response.data.data);
      }
    } catch (error) {
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action will mark the product as inactive.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/products/${id}`);

      if (response.data.success) {
        toast.success(`"${name}" deleted successfully`);
        fetchProducts();
        fetchInventorySummary();
      } else {
        toast.error(response.data.message || "Failed to delete product");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete product");
    }
  };

  const handleViewProduct = (product) => {
    const details = `
Product Details:

Product Code: ${product.product_code}
Product Name: ${product.product_name}
Category: ${product.category_id?.category_name || "N/A"}
UOM: ${product.primary_uom_id?.uom_name || "N/A"} (${product.primary_uom_id?.uom_type || "N/A"})
Type: ${product.product_type}
Costing Method: ${product.product_type === "Stock" ? "Weighted" : product.costing_method}
Taxable: ${product.is_taxable ? "Yes" : "No"}
Reorder Level: ${product.reorder_level}

Inventory:
  Quantity: ${product.current_quantity ?? 0}
  Default Purchase Price: ${formatCurrency(product.unit_price)}
  Average Cost: ${formatCurrency(product.average_cost)}
  Inventory Value: ${formatCurrency(product.inventory_value)}
  Sale Price: ${formatCurrency(product.sale_price)}

Company: ${product.company_id?.company_name || "N/A"}
Created By: ${product.created_by?.name || "System"}
Created: ${new Date(product.created_at).toLocaleDateString()}
Status: ${product.status}
    `;

    alert(details);
  };

  const handleEditProduct = (id) => {
    navigate(`/products/edit/${id}`);
  };

  const getCostBasis = (product) => Number(product.average_cost || product.unit_price || 0);

  const getMargin = (product) => {
    const cost = getCostBasis(product);
    if (!cost || !product.sale_price) return 0;
    return product.sale_price - cost;
  };

  const getMarginPercentage = (product) => {
    const cost = getCostBasis(product);
    if (!cost || cost <= 0) return 0;
    return (getMargin(product) / cost) * 100;
  };

  const clearFilters = () => {
    setSelectedCompany("");
    setSelectedCategory("");
    setSelectedProductType("");
    setSearchTerm("");
    setShowFilters(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="products-container">
      {/* Header */}
      <div className="header">
        <div className="products-header">
          <h2>Products</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>

        <div className="header-actions">
          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filter
          </button>
        </div>
      </div>

      {/* Inventory Summary Cards */}
      {inventorySummary && (
        <div className="inventory-summary">
          <div className="summary-card">
            <div className="summary-icon" style={{ background: '#4f46e5' }}>
              <Package size={18} />
            </div>
            <div className="summary-content">
              <h3>{inventorySummary.totalProducts || 0}</h3>
              <p>Total Products</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon" style={{ background: '#10b981' }}>
              <ShoppingCart size={18} />
            </div>
            <div className="summary-content">
              <h3>{inventorySummary.totalQuantity || 0}</h3>
              <p>Total Quantity</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon" style={{ background: '#f59e0b' }}>
              <Calculator size={18} />
            </div>
            <div className="summary-content">
              <h3>{formatCurrency(inventorySummary.totalValue || 0)}</h3>
              <p>Total Value</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon" style={{ background: '#ef4444' }}>
              <AlertCircle size={18} />
            </div>
            <div className="summary-content">
              <h3>{formatCurrency(inventorySummary.averagePrice || 0)}</h3>
              <p>Average Price</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <div className="filters-grid">
            {isSystemAdmin && (
              <div className="filter-group">
                <label>Company:</label>
                <div className="select-wrapper">
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    disabled={loadingCompanies}
                  >
                    <option value="">All Companies</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
              </div>
            )}

            <div className="filter-group">
              <label>Category:</label>
              <div className="select-wrapper">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={loadingCategories}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
                <Tag size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Product Type:</label>
              <div className="select-wrapper">
                <select
                  value={selectedProductType}
                  onChange={(e) => setSelectedProductType(e.target.value)}
                >
                  {productTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <Package size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <div className="select-wrapper">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      setSearchTerm(prev => prev ? `${prev} status:${e.target.value}` : `status:${e.target.value}`);
                    }
                  }}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <CheckCircle size={16} className="select-icon" />
              </div>
            </div>
          </div>

          <button
            className="clear-filters"
            onClick={clearFilters}
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search products by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="stats">
          <span>Showing: {products.length} products</span>
          {(selectedCompany || selectedCategory || selectedProductType || searchTerm) && (
            <span className="filter-badge" onClick={clearFilters} style={{ cursor: 'pointer' }}>
              <Filter size={12} />
              Filters Active
            </span>
          )}
        </div>
      </div>

      {/* Products Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product Code</th>
              <th>Product Name</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Category</th>
              <th>Type</th>
              <th>UOM</th>
              <th>Costing Method</th>
              <th>Quantity</th>
              <th>Default Price</th>
              <th>Average Cost</th>
              <th>Total Value</th>
              <th>Sale Price</th>
              <th>Margin</th>
              <th>Taxable</th>
              <th>Status</th>
              {/* <th>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 17 : 16} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Package size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany || selectedCategory || selectedProductType
                        ? "No products found matching your criteria"
                        : "No products found"
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              products.map((product, index) => {
                const margin = getMargin(product);
                const marginPercentage = getMarginPercentage(product);
                const marginClass = margin > 0 ? "positive" : margin < 0 ? "negative" : "neutral";

                return (
                  <tr key={product._id}>
                    <td>{index + 1}</td>

                    <td>
                      <div className="product-number">
                        <strong>{product.product_code}</strong>
                      </div>
                    </td>

                    <td>
                      <div className="product-name">
                        <strong>{product.product_name}</strong>
                      </div>
                    </td>

                    {isSystemAdmin && (
                      <td>
                        <div className="company-cell">
                          <Building size={12} className="mr-2" />
                          <div>
                            <div>{product.company_id?.company_name || "N/A"}</div>
                            <small>{product.company_id?.company_code || ""}</small>
                          </div>
                        </div>
                      </td>
                    )}

                    <td>
                      <div className="category-cell">
                        <Tag size={12} className="mr-2" />
                        <span>{product.category_id?.category_name || "N/A"}</span>
                      </div>
                    </td>

                    <td>
                      <span className={`type-badge ${product.product_type.toLowerCase()}`}>
                        {product.product_type}
                      </span>
                    </td>

                    <td>
                      <div className="uom-cell">
                        <Scale size={12} className="mr-2" />
                        <span>
                          {product.primary_uom_id?.uom_name || "N/A"}
                          <small className="uom-type">
                            {product.primary_uom_id?.uom_type ? `(${product.primary_uom_id.uom_type})` : ""}
                          </small>
                        </span>
                      </div>
                    </td>

                    <td>
                      <span className="costing-badge">
                        <BarChart3 size={12} className="mr-2" />
                        {product.product_type === "Stock" ? "Weighted" : product.costing_method}
                      </span>
                    </td>

                    <td>
                      <div className="quantity-cell">
                        <span className={`quantity-value ${(product.available_quantity ?? 0) <= product.reorder_level && (product.available_quantity ?? 0) > 0 ? 'low-stock' : (product.available_quantity ?? 0) === 0 ? 'out-of-stock' : ''}`}>
                          {product.current_quantity ?? 0}
                        </span>
                        <small className="reorder-info">
                          Available: {product.available_quantity ?? 0} | Reserved: {product.reserved_quantity ?? 0}
                        </small>
                        {product.reorder_level > 0 && (
                          <small className="reorder-info">
                            Reorder: {product.reorder_level}
                          </small>
                        )}
                      </div>
                    </td>

                    <td className="price-cell">
                      {formatCurrency(product.unit_price)}
                    </td>

                    <td className="price-cell">
                      {formatCurrency(product.average_cost)}
                    </td>

                    <td className="price-cell total-value">
                      {formatCurrency(product.inventory_value)}
                    </td>

                    <td className="price-cell sale-price">
                      {formatCurrency(product.sale_price)}
                    </td>

                    <td>
                      <div className={`margin-cell ${marginClass}`}>
                        {margin !== 0 ? (
                          <>
                            <TrendingUp size={12} className="mr-1" />
                            {formatCurrency(margin)}
                            <small className="margin-percentage">
                              ({Math.abs(marginPercentage).toFixed(1)}%)
                            </small>
                          </>
                        ) : (
                          "-"
                        )}
                      </div>
                    </td>

                    <td>
                      <span className={`taxable-badge ${product.is_taxable ? 'taxable' : 'non-taxable'}`}>
                        {product.is_taxable ? (
                          <>
                            <DollarSign size={12} className="mr-2" />
                            Taxable
                          </>
                        ) : (
                          "Non-Taxable"
                        )}
                      </span>
                    </td>

                    <td>
                      <span className={`status-badge ${product.status}`}>
                        {product.status === "active" ? (
                          <>
                            <CheckCircle size={12} className="mr-2" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle size={12} className="mr-2" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>

                    {/* <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewProduct(product)}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditProduct(product._id)}
                          title="Edit Product"
                          disabled={product.status === "inactive"}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(product._id, product.product_name)}
                          title="Delete Product"
                          disabled={product.status === "inactive"}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td> */}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductsList;